import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { buildProfilePath } from '../utils/profileUrl';
import toast from 'react-hot-toast';
import { getAvatarUrl } from '../utils/imageUtils';
import api from '../config/api';
import { MessageSquare, Heart, UserPlus } from 'lucide-react';

const NotificationsContext = createContext();

export const useNotifications = () => {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within NotificationsProvider');
  }
  return ctx;
};

export const NotificationsProvider = ({ children }) => {
  const { user } = useAuth();
  const shownNotificationsRef = useRef(new Set());
  const [unreadCount, setUnreadCount] = useState(0);

  const showLikeToast = ({ id, actorId, actorName, actorAvatar, pinId, type, created_at, pinImageUrl, data }) => {
    if (id && shownNotificationsRef.current.has(id)) return;
    if (id) shownNotificationsRef.current.add(id);
    // New domain: follow + message + profile_like.
    // Keep a small compatibility shim for legacy data.
    const kind = type === 'follow'
      ? 'follow'
      : type === 'message'
        ? 'message'
        : type === 'profile_like'
          ? 'profile_like'
          : (pinId ? 'legacy_like' : 'follow');
    toast.custom((t) => (
      <div
        className="flex items-center p-2 rounded-lg border border-white/30 bg-white/70 backdrop-blur-md shadow-lg cursor-pointer"
        onClick={() => {
          // navigate when clicked (window.location used to avoid adding router dependency here)
          if (kind === 'follow' && actorId) {
            // We only have actorId here, so we intentionally use the legacy URL.
            // LegacyUserRedirect will resolve the canonical category-based URL.
            window.location.href = buildProfilePath({ profile: { id: actorId } });
          } else if (kind === 'message') {
            const threadId = data?.threadId || data?.thread_id;
            window.location.href = threadId ? `/messages?thread=${encodeURIComponent(threadId)}` : '/messages';
          } else if (pinId) {
            // Legacy: pin notifications are no longer supported; fallback to home.
            window.location.href = '/';
          } else if (kind === 'profile_like' && actorId) {
            window.location.href = buildProfilePath({ profile: { id: actorId } });
          }
          // mark notification as read (fire-and-forget)
          try {
            if (id) {
              // Call server endpoint to mark single notification read
              const token = localStorage.getItem('token');
              fetch(`/api/notifications/${id}/mark-read`, {
                method: 'POST',
                headers: token
                  ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
                  : { 'Content-Type': 'application/json' },
                body: JSON.stringify({})
              }).catch(() => { });
            }
          } catch (e) { }
          try { shownNotificationsRef.current.delete(id); } catch (err) { }
          toast.dismiss(t.id);
        }}
        // ensure close button isn't clipped and is clickable
        style={{ minWidth: 280, paddingRight: 44, overflow: 'visible', position: 'relative', zIndex: 9999 }}
      >
        {/* Close button */}
        <button
          aria-label="Close notification"
          title="Закрыть"
          onClick={(e) => {
            // prevent the container click (which would navigate/mark-read) when closing
            e.stopPropagation();
            try {
              // mark notification as read in DB
              if (id) {
                const token = localStorage.getItem('token');
                fetch(`/api/notifications/${id}/mark-read`, {
                  method: 'POST',
                  headers: token
                    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
                    : { 'Content-Type': 'application/json' },
                  body: JSON.stringify({})
                }).catch(() => { });
              }
            } catch (err) {
              // ignore errors marking as read
            }
            try { shownNotificationsRef.current.delete(id); } catch (err) { }
            try {
              toast.dismiss(t.id);
            } catch (err) {
              // ignore
            }
          }}
          className="absolute right-2 top-2 w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-700 focus:outline-none"
          style={{ background: 'transparent', border: 'none', padding: 0 }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <div className="flex-shrink-0">
          <img
            src={actorAvatar ? getAvatarUrl(actorAvatar) : '/favicon.ico'}
            alt={actorName || 'User'}
            className="w-10 h-10 rounded-full object-cover mr-3"
          />
        </div>
        <div className="flex-1 text-sm">
          <div className="font-medium text-gray-900 flex items-center gap-1">
            {kind === 'follow' ? <UserPlus className="w-4 h-4 text-primary-500" /> : null}
            {kind === 'message' ? <MessageSquare className="w-4 h-4 text-primary-500" /> : null}
            {kind === 'profile_like' ? <Heart className="w-4 h-4 text-red-500 fill-current" /> : null}

            <span>
              {kind === 'follow'
                ? (actorName ? `${actorName} подписался(ась) на вас` : 'Новый подписчик')
                : kind === 'message'
                  ? (actorName ? `${actorName} написал(а) вам сообщение` : 'Вам написали сообщение')
                  : kind === 'profile_like'
                    ? (actorName ? `${actorName} лайкнул(а) ваш профиль` : 'Ваш профиль лайкнули')
                    : 'Уведомление'
              }
            </span>
          </div>

          {kind === 'message' && (data?.goal || data?.Goal) ? (
            <div className="text-xs text-secondary-600 mt-1">Цель: {data.goal || data.Goal}</div>
          ) : null}

          <div className="text-xs text-secondary-500 mt-1">
            {new Date(created_at || Date.now()).toLocaleString()}
          </div>
        </div>
        {kind !== 'follow' && pinImageUrl ? null : null}
      </div>
    ), { id: `notif-${id}`, duration: 6000 });

    // Notify other UI (e.g. Navbar) that a new notification arrived so they can refresh without reload
    try {
      if (typeof window !== 'undefined' && window.dispatchEvent) {
        const detail = { id, actorName, actorAvatar, pinId, created_at, type: kind };
        window.dispatchEvent(new CustomEvent('notifications:new', { detail }));
      }
    } catch (e) {
      // ignore
    }
  };

  // Polling when Supabase is not configured to get unread notifications without reload
  useEffect(() => {
    if (!user?.id) {
      setUnreadCount(0);
      return;
    }

    let timer = null;
    const seen = shownNotificationsRef.current;

    const tick = async () => {
      try {
        const res = await api.get('/api/notifications?read=false');
        const list = (res && res.data && Array.isArray(res.data.data))
          ? res.data.data
          : (Array.isArray(res.data) ? res.data : []);

        setUnreadCount(Array.isArray(list) ? list.length : 0);

        for (const n of (list || [])) {
          if (!n || !n.id || seen.has(n.id)) continue;
          showLikeToast({
            id: n.id,
            actorId: n.actor_user_id,
            actorName: n.actor_username,
            actorAvatar: n.actor_avatar_url,
            pinId: n.pin_id,
            type: n.type,
            created_at: n.created_at,
            pinImageUrl: n.pin_image_url || n.image_url || n.pin_thumbnail || null,
            data: n.data || null
          });
        }
      } catch (e) {
        // ignore
      }
    };

    // Polling cadence: keep it moderate to avoid load.
    // Also slow down in background tabs.
    const getIntervalMs = () => {
      try {
        return (typeof document !== 'undefined' && document.hidden) ? 180000 : 60000;
      } catch (_) {
        return 60000;
      }
    };

    const start = () => {
      if (timer) {
        try { clearInterval(timer); } catch (e) { }
      }
      timer = setInterval(tick, getIntervalMs());
    };

    start();
    const onVis = () => start();
    try { document.addEventListener('visibilitychange', onVis); } catch (_) { }

    // run immediately once
    try { tick(); } catch (e) { }

    return () => {
      if (timer) {
        try { clearInterval(timer); } catch (e) { }
      }
      try { document.removeEventListener('visibilitychange', onVis); } catch (_) { }
    };
  }, [user?.id]);

  const markAllRead = async () => {
    try {
      await api.post('/api/notifications/mark-all-read');
      setUnreadCount(0);
      // dismiss all toasts shown by this provider
      toast.dismiss();
      //console.log('Marked all notifications as read (client)');
    } catch (e) {
      console.error('Failed to mark all notifications as read:', e);
      toast.error('Ошибка при пометке уведомлений как прочитанных');
    }
  };

  const value = { showLikeToast, markAllRead, unreadCount };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};
